import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import {
  asFeedAdPlacement,
  clampFeedAdSlotEvery,
  DEFAULT_FEED_AD_SLOT_EVERY,
  FEED_AD_BUCKET,
  linkPreviewFromMetadata,
  type AdminFeedAdDetail,
  type AdminFeedAdRow,
  type FeedAd,
  type FeedAdAttachmentInput,
  type FeedAdSettings,
  type FeedAdsPayload,
  type UpsertFeedAdInput,
} from "@/lib/feed/ads";
import {
  attachmentTypeFromMime,
  isAllowedAttachmentType,
  resolvedMimeType,
  type FeedAttachment,
} from "@/lib/feed/types";
import { createId, getErrorMessage, safeStorageExtension } from "@/lib/utils";

function asNumber(value: unknown, fallback = 0) {
  const parsed = Number(value ?? fallback);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function asRows<T>(data: unknown): T[] {
  return Array.isArray(data) ? (data as T[]) : [];
}

function asString(value: unknown) {
  return typeof value === "string" ? value : null;
}

type AttachmentRow = {
  id?: string;
  attachmentType?: string;
  storageBucket?: string | null;
  storagePath?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
};

async function signAdAttachments(
  rows: AttachmentRow[],
): Promise<FeedAttachment[]> {
  const supabase = getSupabaseBrowserClient();
  const unsigned = rows.flatMap((row, index) => {
    const type = row.attachmentType;
    if (
      type !== "image" &&
      type !== "video" &&
      type !== "document"
    ) {
      return [];
    }
    return [
      {
        id: typeof row.id === "string" ? row.id : `ad-att-${index}`,
        type,
        bucket: row.storageBucket ?? null,
        path: row.storagePath ?? null,
        fileName: row.fileName ?? null,
        mimeType: row.mimeType ?? null,
        sizeBytes:
          typeof row.sizeBytes === "number" ? row.sizeBytes : null,
      } satisfies Omit<FeedAttachment, "url">,
    ];
  });

  const paths = unsigned
    .filter((item) => item.bucket === FEED_AD_BUCKET && item.path)
    .map((item) => item.path!);

  const signedUrls = new Map<string, string>();
  if (paths.length > 0) {
    const { data, error } = await supabase.storage
      .from(FEED_AD_BUCKET)
      .createSignedUrls([...new Set(paths)], 60 * 60);
    if (!error) {
      for (const item of data ?? []) {
        if (item.path && item.signedUrl) {
          signedUrls.set(item.path, item.signedUrl);
        }
      }
    }
  }

  return unsigned.map((item) => ({
    ...item,
    url: item.path ? signedUrls.get(item.path) ?? null : null,
  }));
}

function mapAttachmentRows(value: unknown): AttachmentRow[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    return [item as AttachmentRow];
  });
}

function mapFeedAd(row: {
  id: string;
  content: string | null;
  cta_label: string | null;
  cta_url: string | null;
  weight: number;
  metadata: unknown;
  attachments: unknown;
}): Omit<FeedAd, "attachments"> & { attachmentRows: AttachmentRow[] } {
  return {
    id: row.id,
    content: row.content,
    ctaLabel: row.cta_label,
    ctaUrl: row.cta_url,
    weight: asNumber(row.weight, 5),
    linkPreview: linkPreviewFromMetadata(row.metadata),
    attachmentRows: mapAttachmentRows(row.attachments),
  };
}

function parseSlotEvery(value: unknown) {
  if (!value || typeof value !== "object") {
    return DEFAULT_FEED_AD_SLOT_EVERY;
  }
  return clampFeedAdSlotEvery(
    asNumber((value as { slotEvery?: unknown }).slotEvery, DEFAULT_FEED_AD_SLOT_EVERY),
  );
}

export async function getFeedAdSettings(): Promise<FeedAdSettings> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_feed_ad_settings");
  if (error) {
    throw error;
  }
  return { slotEvery: parseSlotEvery(data) };
}

export async function setAdminFeedAdSettings(
  slotEvery: number,
): Promise<FeedAdSettings> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("set_admin_feed_ad_settings", {
    next_slot_every: clampFeedAdSlotEvery(slotEvery),
  });
  if (error) {
    throw error;
  }
  return { slotEvery: parseSlotEvery(data) };
}

export async function listFeedAds(
  placement: "home" | "community",
  communityId?: string | null,
): Promise<FeedAdsPayload> {
  const supabase = getSupabaseBrowserClient();
  const [adsResult, settingsResult] = await Promise.all([
    supabase.rpc("list_feed_ads", {
      p_placement: placement,
      p_community_id: communityId ?? null,
    }),
    supabase.rpc("get_feed_ad_settings"),
  ]);

  if (adsResult.error) {
    throw adsResult.error;
  }
  if (settingsResult.error) {
    throw settingsResult.error;
  }

  const mapped = asRows<{
    id: string;
    content: string | null;
    cta_label: string | null;
    cta_url: string | null;
    weight: number;
    metadata: unknown;
    attachments: unknown;
  }>(adsResult.data).map(mapFeedAd);

  const ads = await Promise.all(
    mapped.map(async (item) => ({
      id: item.id,
      content: item.content,
      ctaLabel: item.ctaLabel,
      ctaUrl: item.ctaUrl,
      weight: item.weight,
      linkPreview: item.linkPreview,
      attachments: await signAdAttachments(item.attachmentRows),
    })),
  );

  return {
    ads,
    slotEvery: parseSlotEvery(settingsResult.data),
  };
}

export async function listAdminFeedAds(): Promise<AdminFeedAdRow[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_admin_feed_ads");

  if (error) {
    throw error;
  }

  return asRows<{
    id: string;
    title: string;
    content_preview: string | null;
    is_active: boolean;
    placement: string;
    weight: number;
    starts_at: string | null;
    ends_at: string | null;
    community_names: string | null;
    attachment_count: number;
    created_at: string;
  }>(data).map((row) => ({
    id: row.id,
    title: row.title,
    contentPreview: row.content_preview,
    isActive: Boolean(row.is_active),
    placement: asFeedAdPlacement(row.placement),
    weight: asNumber(row.weight, 5),
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    communityNames: row.community_names,
    attachmentCount: asNumber(row.attachment_count),
    createdAt: row.created_at,
  }));
}

export async function getAdminFeedAd(
  adId: string,
): Promise<AdminFeedAdDetail> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_admin_feed_ad", {
    target_ad_id: adId,
  });

  if (error) {
    throw error;
  }

  const payload =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const communityIds = Array.isArray(payload.communityIds)
    ? payload.communityIds.filter((value): value is string => typeof value === "string")
    : [];

  return {
    id: typeof payload.id === "string" ? payload.id : adId,
    title: typeof payload.title === "string" ? payload.title : "",
    content: asString(payload.content),
    ctaLabel: asString(payload.ctaLabel),
    ctaUrl: asString(payload.ctaUrl),
    weight: asNumber(payload.weight, 5),
    placement: asFeedAdPlacement(String(payload.placement ?? "home")),
    startsAt: asString(payload.startsAt),
    endsAt: asString(payload.endsAt),
    isActive: payload.isActive === true,
    communityIds,
    linkPreview: linkPreviewFromMetadata(payload.metadata),
    attachments: await signAdAttachments(mapAttachmentRows(payload.attachments)),
  };
}

export async function upsertAdminFeedAd(input: UpsertFeedAdInput) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("upsert_admin_feed_ad", {
    payload: {
      id: input.id ?? null,
      title: input.title,
      content: input.content,
      ctaLabel: input.ctaLabel,
      ctaUrl: input.ctaUrl,
      weight: input.weight,
      placement: input.placement,
      startsAt: input.startsAt,
      endsAt: input.endsAt,
      isActive: input.isActive,
      communityIds: input.communityIds,
      attachments: input.attachments,
      linkPreview: input.linkPreview,
    },
  });

  if (error) {
    throw error;
  }

  return String(data);
}

export async function deleteAdminFeedAd(adId: string) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("delete_admin_feed_ad", {
    target_ad_id: adId,
  });

  if (error) {
    throw error;
  }
}

export async function uploadFeedAdAttachments(files: File[], userId: string) {
  const supabase = getSupabaseBrowserClient();
  const uploaded: FeedAdAttachmentInput[] = [];

  for (const file of files) {
    const mimeType = resolvedMimeType(file);
    if (!isAllowedAttachmentType(mimeType)) {
      throw new Error("Nem támogatott melléklettípus.");
    }
    const objectPath = `${userId}/${createId()}.${safeStorageExtension(file.name)}`;
    const { error } = await supabase.storage
      .from(FEED_AD_BUCKET)
      .upload(objectPath, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: mimeType,
      });

    if (error) {
      await removeFeedAdAttachments(uploaded);
      throw error;
    }

    uploaded.push({
      attachmentType: attachmentTypeFromMime(mimeType),
      storageBucket: FEED_AD_BUCKET,
      storagePath: objectPath,
      fileName: file.name,
      mimeType,
      sizeBytes: file.size,
    });
  }

  return uploaded;
}

export async function removeFeedAdAttachments(
  attachments: Pick<FeedAdAttachmentInput, "storageBucket" | "storagePath">[],
) {
  const paths = attachments
    .filter((item) => item.storageBucket === FEED_AD_BUCKET)
    .map((item) => item.storagePath);

  if (paths.length === 0) {
    return;
  }

  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.storage.from(FEED_AD_BUCKET).remove(paths);
  if (error) {
    throw new Error(getErrorMessage(error));
  }
}
